import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { formatDateTime, formatRelative } from '../../lib/formatUtils';
import { useNow } from '../../lib/useNow';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, Phone, MessageCircle, Mail, MapPin, Star, ExternalLink,
  Clock, MessageSquare, PhoneCall, FileText, Zap, CalendarPlus, CheckCircle2, AlertTriangle, ArrowRight,
  Flame, Snowflake, Pause, Send, Loader2 as Spinner, MousePointerClick, RotateCcw, ArrowRightLeft,
  Sparkles, IndianRupee, ClipboardEdit, Eye, Megaphone, PartyPopper, Wallet, HandshakeIcon,
  PhoneOutgoing, Globe2, RefreshCw, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { UserPicker } from '../../components/UserPicker';
import { OutreachTimeline } from '../../components/smb/OutreachTimeline';
import { ActivationLinksPanel } from '../../components/smb/ActivationLinksPanel';
import { ManualPaymentDialog } from '../../components/smb/ManualPaymentDialog';
import { AddonsPanel } from '../../components/smb/AddonsPanel';
import { BackfillInvoicePanel } from '../../components/smb/BackfillInvoicePanel';
import { CombinedPaymentDialog } from '../../components/smb/CombinedPaymentDialog';
import { LeadImagesGallery } from '../../components/smb/LeadImagesGallery';
import { ActivationSubmissionCard } from '../../components/smb/ActivationSubmissionCard';
import { ProjectIdBadge } from '../../components/smb/ProjectIdBadge';
import { useAuth } from '../../context/AuthContext';

const STATUS_COLORS = {
  new: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  preview_ready: 'bg-violet-500/15 text-violet-400 border-violet-500/30',
  preview_qa_failed: 'bg-red-500/15 text-red-400 border-red-500/30',
  preview_qa_passed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  contacted: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  call_declined: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  preview_sent: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  interested: 'bg-green-500/15 text-green-400 border-green-500/30',
  follow_up: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  interest_lost: 'bg-rose-500/15 text-rose-400 border-rose-500/30',
  commercials_shared: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  payment_received: 'bg-green-600/15 text-green-500 border-green-600/30',
  tweaks_in_progress: 'bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30',
  live_ready: 'bg-violet-600/15 text-violet-400 border-violet-600/30',
  live_deployed: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  live_qa_failed: 'bg-red-600/15 text-red-500 border-red-600/30',
  live_qa_passed: 'bg-emerald-600/15 text-emerald-500 border-emerald-600/30',
  live_sent: 'bg-blue-600/15 text-blue-400 border-blue-600/30',
  lost: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
};

// Maps audit-event keys (smb_status_history.event) to lucide icons so
// non-transition events render with a meaningful symbol instead of the
// generic from→to status badge pair. See backend `_audit_event`.
const EVENT_ICONS = {
  activation_link_sent: Send,
  activation_payment_received: IndianRupee,
  full_payment_received: Wallet,
  details_submitted: FileText,
  submission_edited: ClipboardEdit,
  live_preview_sent: Eye,
  final_payment_link_sent: IndianRupee,
  customer_tapped_make_it_live: PartyPopper,
  customer_tapped_need_changes: RotateCcw,
  customer_tapped_call_me: PhoneOutgoing,
  website_published_live: Globe2,
  reminder_r1_sent: Bell,
  reminder_r2_sent: Bell,
  reminder_r3_sent: Bell,
  reminder_r4_sent: Bell,
  payment_reconciled: RefreshCw,
};

// Turn a `changed_by` value into a human label. The backend uses three
// special string values when there's no real user:
//   "system"            → background CRON / webhook
//   "customer:whatsapp" → customer-initiated WhatsApp quick-reply tap
//   "customer:razorpay" → customer payment captured via Razorpay
const formatActor = (rawId, resolvedName) => {
  if (resolvedName) return resolvedName;
  if (!rawId || rawId === 'system') return 'System';
  if (rawId === 'customer:whatsapp') return 'Customer (WhatsApp)';
  if (rawId === 'customer:razorpay') return 'Customer (Razorpay)';
  if (typeof rawId === 'string' && rawId.startsWith('customer:')) {
    return `Customer (${rawId.slice('customer:'.length)})`;
  }
  return 'System';
};

const ACTIVITY_ICONS = {
  call: PhoneCall, whatsapp: MessageCircle, note: FileText, status_change: Zap, demo: ExternalLink,
};

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Call' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'note', label: 'Note' },
  { value: 'demo', label: 'Demo/Review' },
];

export const SMBLeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // `smb.outreach` is now strictly the Outreach Automation gate — Start /
  // Restart / Stop / Reset & Retry of the 3-message WhatsApp+Email cadence,
  // plus the read-only "View-only access" notice. The activation-flow
  // actions (Send Activation Link, Reconcile Payment, Convert to Custom
  // Project) only require `smb.view` — handled implicitly by the route
  // guard since this page itself is `smb.view`-gated. Read-only outreach
  // stats remain visible to anyone with `smb.view` (handled server-side).
  const canManageOutreach = hasPermission('smb.outreach');
  // Forces the "Actions › last updated" chip to re-evaluate every 30s so
  // it ticks ("5m ago" → "6m ago") even while the user keeps the page open.
  useNow(30_000);
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [activityForm, setActivityForm] = useState({ type: 'note', description: '' });
  const [outreach, setOutreach] = useState(null);
  const [outreachBusy, setOutreachBusy] = useState(false);
  const [followupForm, setFollowupForm] = useState({ followup_datetime: '', note: '', assigned_to: '', update_status: true });
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [convertForm, setConvertForm] = useState({ converted_amount: '', notes: '' });
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [statusModal, setStatusModal] = useState(false);
  const [statusValue, setStatusValue] = useState('');
  const [statusRemark, setStatusRemark] = useState('');
  const [statusPreviewUrl, setStatusPreviewUrl] = useState('');
  const [statusLiveUrl, setStatusLiveUrl] = useState('');
  const [smbProjectConverting, setSmbProjectConverting] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcilePayId, setReconcilePayId] = useState('');
  const [reconcileBusy, setReconcileBusy] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [manualPayOpen, setManualPayOpen] = useState(false);
  const [manualPayAddon, setManualPayAddon] = useState(null);
  const [combinedPayOpen, setCombinedPayOpen] = useState(false);
  const [leadAddons, setLeadAddons] = useState([]);
  const [smbPackage, setSmbPackage] = useState(null);

  const handleReconcilePayment = async () => {
    const pid = reconcilePayId.trim();
    if (!pid) { toast.error('Paste the Razorpay payment id (pay_XXXXXX)'); return; }
    if (!pid.startsWith('pay_')) {
      if (!window.confirm("That doesn't look like a Razorpay payment id (should start with 'pay_'). Continue anyway?")) return;
    }
    setReconcileBusy(true);
    setReconcileResult(null);
    try {
      const r = await api.post(`/smb/leads/${id}/reconcile-payment`, {
        razorpay_payment_id: pid,
      });
      setReconcileResult(r.data);
      if (r.data?.ok) {
        toast.success(r.data.duplicate
          ? 'Already reconciled — lead is up to date'
          : 'Payment reconciled — lead advanced to Activation Paid');
        await fetchLead();
      } else {
        toast.error(r.data?.msg || 'Reconcile failed');
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      setReconcileResult({ ok: false, error: detail });
      toast.error(detail);
    } finally { setReconcileBusy(false); }
  };

  const handleConvertToCustomProject = async () => {
    if (smbProjectConverting || !lead?.smb_project_id) return;
    if (!window.confirm(
      `Convert this lead to a full custom project?\n\n` +
      `• A new row will be created in the main Projects module.\n` +
      `• A client will be auto-created (or linked) from this lead's contact details.\n` +
      `• This lead will be archived from the SMB activation pipeline.\n\n` +
      `Proceed?`
    )) return;
    setSmbProjectConverting(true);
    try {
      const r = await api.post(`/smb/activation/${id}/convert-to-custom-project`, {});
      if (r.data?.success) {
        toast.success(r.data.already_converted
          ? 'Already converted — opening project'
          : `Converted · client: ${r.data.client?.name || 'auto-created'}`);
        const pid = r.data.data?.id;
        if (pid) navigate(`/projects/${pid}`);
      }
    } catch (er) {
      toast.error(er.response?.data?.detail || 'Conversion failed');
    } finally {
      setSmbProjectConverting(false);
    }
  };

  const PREVIEW_URL_STATUSES = ['preview_ready', 'preview_qa_passed', 'preview_qa_failed', 'preview_sent', 'tweaks_in_progress', 'live_ready', 'live_qa_passed', 'live_qa_failed', 'live_sent', 'live_deployed'];
  const LIVE_URL_STATUSES = ['live_ready', 'live_qa_passed', 'live_qa_failed', 'live_sent', 'live_deployed'];

  const fetchLead = useCallback(async () => {
    try {
      const res = await api.get(`/smb/leads/${id}`);
      if (res.data.success) setLead(res.data.data);
    } catch { toast.error('Failed to load lead'); navigate('/smb/leads'); }
  }, [id, navigate]);

  const fetchActivities = useCallback(async () => {
    try {
      const res = await api.get(`/smb/leads/${id}/activities`);
      if (res.data.success) setActivities(res.data.data);
    } catch {}
  }, [id]);

  const fetchFollowups = useCallback(async () => {
    try {
      const res = await api.get(`/smb/leads/${id}/followups`);
      if (res.data.success) setFollowups(res.data.data);
    } catch {}
  }, [id]);

  const fetchStatusHistory = useCallback(async () => {
    try {
      const res = await api.get(`/smb/leads/${id}/status-history`);
      if (res.data.success) setStatusHistory(res.data.data);
    } catch {}
  }, [id]);

  const fetchOutreach = useCallback(async () => {
    try {
      const res = await api.get(`/smb/outreach/${id}/status`);
      if (res.data.success) setOutreach(res.data.data);
    } catch {}
  }, [id]);

  useEffect(() => {
    fetchLead(); fetchActivities(); fetchFollowups(); fetchStatusHistory(); fetchOutreach();
    const loadMeta = async () => {
      try {
        const [sRes, uRes] = await Promise.all([api.get('/smb/statuses'), api.get('/smb/users')]);
        if (sRes.data.success) setStatuses(sRes.data.data);
        if (uRes.data.success) setUsers(uRes.data.data);
      } catch {}
    };
    loadMeta();
  }, [fetchLead, fetchActivities, fetchFollowups, fetchStatusHistory, fetchOutreach]);

  // Load the default SMB package for auto-filling manual-payment amounts.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/smb/packages');
        const list = r.data?.data || r.data || [];
        const active = list.filter((p) => p.is_active !== false);
        setSmbPackage(active.find((p) => p.is_default) || active[0] || null);
      } catch {}
    })();
  }, []);

  // Load add-ons whenever the lead changes so the Combined-payment
  // dialog can populate its add-on picker without an extra round-trip.
  useEffect(() => {
    if (!lead?.id) return;
    (async () => {
      try {
        const r = await api.get(`/smb/leads/${lead.id}/addons`);
        setLeadAddons(r.data?.data || []);
      } catch {}
    })();
  }, [lead?.id]);

  const handleStartOutreach = async () => {
    setOutreachBusy(true);
    try {
      await api.post(`/smb/outreach/start/${id}`);
      toast.success('Outreach started');
      fetchOutreach();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start outreach');
    } finally { setOutreachBusy(false); }
  };

  const handleStopOutreach = async () => {
    setOutreachBusy(true);
    try {
      await api.post(`/smb/outreach/stop/${id}`);
      toast.success('Outreach stopped');
      fetchOutreach();
    } catch {
      toast.error('Failed');
    } finally { setOutreachBusy(false); }
  };

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const handleResetOutreach = async () => {
    setOutreachBusy(true);
    try {
      const r = await api.post(`/smb/outreach/reset/${id}`);
      const sendErr = r?.data?.send_error;
      if (sendErr) {
        toast.error(`Reset done, but M1 send failed: ${sendErr}`);
      } else {
        toast.success('Outreach reset — M1 sent now');
      }
      setResetConfirmOpen(false);
      fetchOutreach();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset outreach');
    } finally { setOutreachBusy(false); }
  };

  // Step 1 of the SMB Website Activation Flow — operator triggers
  // when the lead has confirmed positively (over phone, or after
  // manual classification of a non-button reply). Generates the
  // unique activation link, fires WhatsApp Template #1, and pivots
  // the lead status to `activation_link_sent`.
  const [activationBusy, setActivationBusy] = useState(false);
  const handleSendActivationLink = async () => {
    if (activationBusy) return;
    setActivationBusy(true);
    try {
      const res = await api.post(`/smb/activation/trigger/${id}`);
      if (res.data?.success) {
        const wa = res.data.whatsapp || {};
        const reusedNote = res.data.token_reused ? ' (existing link reused — same URL as before)' : '';
        if (wa.ok && !wa.stub) {
          toast.success(`Activation link sent on WhatsApp${reusedNote}`);
        } else if (wa.ok && wa.stub) {
          toast.warning('Activation link generated — but WhatsApp is in STUB mode (no real send). Configure WhatsApp credentials in backend/.env.');
        } else if (wa.skipped_reason === 'no_recipient') {
          toast.warning('Activation link generated — no phone on lead, so no WhatsApp send.');
        } else if (wa.skipped_reason && wa.skipped_reason.endsWith('_not_set')) {
          toast.warning(`Activation link generated — but ${wa.skipped_reason.replace('_not_set','')} template name is not configured. Set it in Settings → SMB Activation Templates.`);
        } else if (wa.error) {
          toast.error(`Activation link generated, but WhatsApp send failed: ${String(wa.error).slice(0, 200)}`);
        } else {
          toast.success('Activation link generated.');
        }
        fetchLead();
      } else {
        toast.error(res.data?.message || 'Failed to send activation link');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send activation link');
    } finally { setActivationBusy(false); }
  };

  const openStatusModal = () => {
    setStatusValue(lead.status);
    setStatusRemark('');
    setStatusPreviewUrl(lead.preview_url || '');
    setStatusLiveUrl(lead.live_url || '');
    setStatusModal(true);
  };

  const isValidUrl = (str) => {
    try { const u = new URL(str); return u.protocol === 'http:' || u.protocol === 'https:'; } catch { return false; }
  };

  const handleStatusChange = async () => {
    if (PREVIEW_URL_STATUSES.includes(statusValue) && !statusPreviewUrl.trim()) {
      toast.error('Preview URL is required for this status');
      return;
    }
    if (PREVIEW_URL_STATUSES.includes(statusValue) && statusPreviewUrl.trim() && !isValidUrl(statusPreviewUrl.trim())) {
      toast.error('Preview URL must be a valid URL (e.g. https://...)');
      return;
    }
    if (LIVE_URL_STATUSES.includes(statusValue) && !statusLiveUrl.trim()) {
      toast.error('Live URL is required for this status');
      return;
    }
    if (LIVE_URL_STATUSES.includes(statusValue) && statusLiveUrl.trim() && !isValidUrl(statusLiveUrl.trim())) {
      toast.error('Live URL must be a valid URL (e.g. https://...)');
      return;
    }
    try {
      await api.put(`/smb/leads/${id}/status`, {
        status: statusValue,
        remark: statusRemark,
        preview_url: statusPreviewUrl,
        live_url: statusLiveUrl,
      });
      toast.success('Status updated');
      setStatusModal(false);
      fetchLead(); fetchActivities(); fetchStatusHistory();
    } catch { toast.error('Failed'); }
  };

  const handleAssign = async (userId) => {
    try {
      await api.put(`/smb/leads/${id}/assign`, { assigned_to: userId });
      toast.success('Lead assigned');
      fetchLead(); fetchActivities();
    } catch { toast.error('Failed'); }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!activityForm.description.trim()) return;
    try {
      await api.post(`/smb/leads/${id}/activities`, activityForm);
      toast.success('Activity logged');
      setActivityForm({ type: 'note', description: '' });
      fetchActivities();
    } catch { toast.error('Failed'); }
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/smb/leads/${id}/followups`, followupForm);
      toast.success('Follow-up scheduled');
      setFollowupForm({ followup_datetime: '', note: '', assigned_to: '', update_status: true });
      setShowFollowupForm(false);
      fetchFollowups();
      if (followupForm.update_status) { fetchLead(); fetchStatusHistory(); }
    } catch { toast.error('Failed'); }
  };

  const handleCompleteFollowup = async (fId) => {
    try {
      await api.put(`/smb/followups/${fId}/complete`);
      toast.success('Follow-up completed');
      fetchFollowups(); fetchActivities();
    } catch { toast.error('Failed'); }
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/smb/leads/${id}/convert`, {
        converted_amount: parseFloat(convertForm.converted_amount) || 0,
        notes: convertForm.notes,
      });
      toast.success('Lead converted!');
      setShowConvertForm(false);
      fetchLead(); fetchActivities();
    } catch { toast.error('Failed'); }
  };

  if (!lead) return <div className="flex items-center justify-center min-h-[50vh] text-muted-foreground">Loading...</div>;

  const isOverdue = (dt) => new Date(dt) < new Date();

  return (
    <div className="space-y-5 animate-fade-in" data-testid="smb-lead-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/smb/leads')} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold">{lead.business_name}</h1>
            {lead.smb_project_id && <ProjectIdBadge fullId={lead.smb_project_id} />}
          </div>
          <p className="text-sm text-muted-foreground capitalize">{lead.category} &middot; {lead.area}, {lead.city}</p>
        </div>
        {!['activation_link_sent', 'activation_paid', 'details_submitted', 'in_development', 'awaiting_approval', 'approved', 'paid', 'website_live'].includes(lead.status) && (
          <Button
            onClick={handleSendActivationLink}
            disabled={activationBusy}
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="send-activation-link-btn"
          >
            {activationBusy ? 'Sending…' : 'Send Activation Link'}
          </Button>
        )}
        {['activation_link_sent', 'activation_paid', 'details_submitted', 'in_development', 'awaiting_approval', 'approved', 'paid', 'website_live'].includes(lead.status) && (
          <Badge className="border-emerald-500/40 text-emerald-500 bg-emerald-500/10 text-xs" data-testid="activation-sent-pill">
            Activation flow active
          </Badge>
        )}
        {lead.status === 'activation_link_sent' && (
          <Button
            onClick={() => setReconcileOpen(true)}
            variant="outline"
            size="sm"
            className="border-amber-500/40 text-amber-500 hover:bg-amber-500/10"
            data-testid="reconcile-payment-btn"
            title="Manually reconcile a successful Razorpay payment that didn't auto-advance"
          >
            Reconcile payment
          </Button>
        )}
        {/* Record off-gateway payment (NEFT / UPI direct / Cash / Cheque).
            Shown as soon as an activation link is sent — and remains
            available across every downstream status (including
            `converted`) so accounts can log payments they receive at
            any point in the lifecycle (e.g., the customer paid an
            add-on / balance after the lead was manually marked
            converted). */}
        {['activation_link_sent', 'converted', 'activation_paid', 'details_submitted', 'in_development', 'awaiting_approval', 'approved', 'paid', 'website_live', 'completed'].includes(lead.status) && (
          <>
            <Button
              onClick={() => setManualPayOpen(true)}
              variant="outline"
              size="sm"
              className="border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
              data-testid="record-manual-payment-btn"
              title="Record a NEFT / UPI / Cash / Cheque payment. Auto-generates a tax invoice."
            >
              <IndianRupee className="h-3.5 w-3.5 mr-1" />
              Record Manual Payment
            </Button>
            <Button
              onClick={() => setCombinedPayOpen(true)}
              variant="outline"
              size="sm"
              className="border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
              data-testid="record-combined-payment-btn"
              title="One deposit that covers multiple line items (e.g. ₹6000 balance + ₹1000 add-on)"
            >
              Combined Payment
            </Button>
          </>
        )}
        {lead.smb_project_id && !lead.converted_to_project_id && (
          <Button
            onClick={handleConvertToCustomProject}
            disabled={smbProjectConverting}
            variant="outline"
            size="sm"
            className="border-primary/40 text-primary hover:bg-primary/10"
            data-testid="convert-to-custom-project-btn"
            title="Convert to a full custom project (auto-creates client)"
          >
            <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
            {smbProjectConverting ? 'Converting…' : 'Convert to Custom Project'}
          </Button>
        )}
        {lead.converted_to_project_id && (
          <Badge
            onClick={() => navigate(`/projects/${lead.converted_to_project_id}`)}
            className="border-primary/40 text-primary bg-primary/10 text-xs cursor-pointer hover:bg-primary/20"
            data-testid="converted-project-pill"
            title="Click to open the converted project"
          >
            <ArrowRightLeft className="h-3 w-3 mr-1" />Converted to project
          </Badge>
        )}
        <Badge className={`${STATUS_COLORS[lead.status]} border text-sm px-3 py-1`}>{statuses.find(s => s.value === lead.status)?.label || lead.status}</Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left Column - Business Info */}
        <div className="space-y-5">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Business Info</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><span>{lead.contact_name}</span></div>
              {lead.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /><a href={`tel:${lead.phone}`} className="text-primary hover:underline">{lead.phone}</a></div>}
              {lead.whatsapp && <div className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-muted-foreground" /><a href={`https://wa.me/${lead.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer" className="text-green-400 hover:underline">{lead.whatsapp}</a></div>}
              {lead.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /><span>{lead.email}</span></div>}
              {lead.google_maps_url && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /><a href={lead.google_maps_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">View on Maps</a></div>}
              {lead.google_rating && <div className="flex items-center gap-2"><Star className="h-4 w-4 text-yellow-500 fill-yellow-500" /><span>{lead.google_rating} / 5</span></div>}
              {lead.notes && <div className="pt-2 border-t border-border"><p className="text-muted-foreground">{lead.notes}</p></div>}
            </CardContent>
          </Card>

          {/* Links */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Links</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              {lead.preview_url ? (
                <a href={lead.preview_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-violet-400 hover:underline" data-testid="preview-link"><ExternalLink className="h-4 w-4" />Preview Site</a>
              ) : <p className="text-muted-foreground">No preview yet</p>}
              {lead.live_url ? (
                <a href={lead.live_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-green-400 hover:underline" data-testid="live-link"><ExternalLink className="h-4 w-4" />Live Site</a>
              ) : <p className="text-muted-foreground">No live site</p>}
            </CardContent>
          </Card>

          {/* Activation Links — sales team copy-paste UX (Feb 2026).
              Sits directly under the Links card so reps can grab the URL
              without scrolling further. */}
          <ActivationLinksPanel leadId={lead.id} />

          {/* Recover Missing Invoices — pre-deploy orphan txns */}
          <BackfillInvoicePanel
            lead={lead}
            onRecovered={() => { fetchLead(); fetchActivities(); fetchStatusHistory(); }}
          />

          {/* Add-ons & Customization charges — incremental billing on
              top of the base package. Auto-generates a tax invoice per
              paid add-on. */}
          <AddonsPanel
            lead={lead}
            onManualPay={(addon) => { setManualPayAddon(addon); setManualPayOpen(true); }}
          />

          {/* Outreach Automation */}
          <Card data-testid="outreach-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" />Outreach Automation</CardTitle>
                {outreach && (
                  <Badge className={
                    outreach.temperature === 'hot' ? 'bg-red-500/15 text-red-400 border-red-500/30' :
                    outreach.temperature === 'warm' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' :
                    outreach.temperature === 'cold' ? 'bg-sky-500/15 text-sky-400 border-sky-500/30' :
                    'bg-muted text-muted-foreground'
                  }>
                    {outreach.temperature === 'hot' && <Flame className="h-3 w-3 mr-1" />}
                    {outreach.temperature === 'cold' && <Snowflake className="h-3 w-3 mr-1" />}
                    {outreach.temperature.toUpperCase()}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!outreach || !outreach.job ? (
                <>
                  <p className="text-muted-foreground text-xs">No outreach yet. {canManageOutreach ? 'Set a preview URL to auto-start the 3-message cadence (WhatsApp + Email).' : 'View-only access — ask an admin to manage outreach.'}</p>
                  {canManageOutreach && (
                    <Button size="sm" onClick={handleStartOutreach} disabled={outreachBusy || !lead.preview_url} className="w-full" data-testid="start-outreach-btn">
                      {outreachBusy ? <Spinner className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      Start Outreach
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-muted/30 p-2">
                      <p className="text-[10px] text-muted-foreground uppercase">Messages</p>
                      <p className="text-lg font-bold" data-testid="msgs-sent">{outreach.job.messages_sent}/3</p>
                    </div>
                    <div className="rounded-lg bg-muted/30 p-2">
                      <p className="text-[10px] text-muted-foreground uppercase flex items-center justify-center gap-1"><MousePointerClick className="h-3 w-3" />Clicks</p>
                      <p className="text-lg font-bold">{outreach.clicks?.length || 0}</p>
                    </div>
                    <div className="rounded-lg bg-muted/30 p-2">
                      <p className="text-[10px] text-muted-foreground uppercase">State</p>
                      <p className={`text-xs font-bold uppercase ${outreach.job.state === 'active' ? 'text-emerald-400' : 'text-muted-foreground'}`}>{outreach.job.state}</p>
                    </div>
                  </div>
                  {outreach.job.state === 'active' && outreach.job.next_send_at && (
                    <p className="text-xs text-muted-foreground"><Clock className="h-3 w-3 inline mr-1" />Next send: {formatDateTime(outreach.job.next_send_at)}</p>
                  )}
                  {outreach.job.state === 'stopped' && outreach.job.stop_reason && (
                    <p className="text-xs text-muted-foreground">Stopped: <span className="font-medium text-foreground">{outreach.job.stop_reason.replace(/_/g, ' ')}</span></p>
                  )}
                  {outreach.messages && outreach.messages.length > 0 && (
                    <div className="space-y-1 pt-2 border-t border-border">
                      <p className="text-[10px] text-muted-foreground uppercase">Recent</p>
                      {outreach.messages.slice(0, 4).map(m => (
                        <div key={m.id} className="flex items-center gap-2 text-xs">
                          <Badge variant="outline" className="text-[10px] py-0 px-1.5">M{m.message_number}</Badge>
                          <span className="capitalize">{m.channel}</span>
                          <Badge className={`text-[10px] py-0 px-1.5 ${m.status === 'sent' ? 'bg-emerald-500/10 text-emerald-400' : m.status === 'failed' ? 'bg-red-500/10 text-red-400' : 'bg-muted text-muted-foreground'}`}>{m.status}{m.stub ? ' (stub)' : ''}</Badge>
                          <span className="text-muted-foreground ml-auto">{formatDateTime(m.sent_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    {!canManageOutreach ? (
                      <p className="text-[11px] text-muted-foreground italic flex-1" data-testid="outreach-readonly-notice">View-only access — ask an admin to manage outreach.</p>
                    ) : outreach.job.state === 'active' ? (
                      <Button size="sm" variant="outline" onClick={handleStopOutreach} disabled={outreachBusy} className="flex-1" data-testid="stop-outreach-btn">
                        <Pause className="h-4 w-4 mr-2" />Stop
                      </Button>
                    ) : (
                      <Button size="sm" onClick={handleStartOutreach} disabled={outreachBusy || !lead.preview_url} className="flex-1" data-testid="restart-outreach-btn">
                        <Send className="h-4 w-4 mr-2" />Restart
                      </Button>
                    )}
                    {canManageOutreach && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setResetConfirmOpen(true)}
                        disabled={outreachBusy}
                        className="flex-1 border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10"
                        data-testid="reset-outreach-btn"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />Reset &amp; Retry
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Customer-submitted Activation Form details — visible once
              the customer has completed the activation form on the public
              page (status = details_submitted onward). */}
          <ActivationSubmissionCard
            leadId={id}
            leadStatus={lead.status}
            onChanged={fetchStatusHistory}
          />

          {/* Automation Timeline (per-lead audit trail of the outreach engine) */}
          <OutreachTimeline leadId={id} />

          {/* Business Images uploaded by the data-collector agents (Google Maps)
              and/or admins manually attaching reference photos. */}
          <LeadImagesGallery leadId={id} />
        </div>

        {/* Right Column — Actions first (most-used controls), then Follow-ups */}
        <div className="space-y-5">
          {/* Actions */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
              <CardTitle className="text-base">Actions</CardTitle>
              {lead.updated_at && (
                <span
                  className="text-[11px] text-muted-foreground inline-flex items-center gap-1 leading-none"
                  title={`Last updated ${formatDateTime(lead.updated_at)}`}
                  data-testid="actions-last-updated"
                >
                  <Clock className="h-3 w-3" />
                  {formatRelative(lead.updated_at)}
                </span>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Change Status</Label>
                <Button variant="outline" className="w-full justify-between" onClick={openStatusModal} data-testid="detail-status-btn">
                  {statuses.find(s => s.value === lead.status)?.label || lead.status}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Assign To</Label>
                <UserPicker value={lead.assigned_to || ''} onChange={handleAssign} placeholder="Search employees..." data-testid="detail-assign-select" />
              </div>
              {lead.status !== 'converted' && lead.status !== 'lost' && (
                <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => setShowConvertForm(!showConvertForm)} data-testid="convert-btn">Mark as Converted</Button>
              )}
            </CardContent>
          </Card>

          {showConvertForm && (
            <Card className="border-green-500/30">
              <CardContent className="pt-4">
                <form onSubmit={handleConvert} className="space-y-3">
                  <div className="space-y-1.5"><Label>Amount (INR)</Label><Input type="number" value={convertForm.converted_amount} onChange={e => setConvertForm({ ...convertForm, converted_amount: e.target.value })} required data-testid="convert-amount" /></div>
                  <div className="space-y-1.5"><Label>Notes</Label><Input value={convertForm.notes} onChange={e => setConvertForm({ ...convertForm, notes: e.target.value })} data-testid="convert-notes" /></div>
                  <Button type="submit" className="w-full" data-testid="convert-submit">Confirm Conversion</Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Follow-ups */}
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Follow-ups</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setShowFollowupForm(!showFollowupForm)} data-testid="add-followup-btn"><CalendarPlus className="h-4 w-4 mr-1" />Add</Button>
            </CardHeader>
            <CardContent>
              {showFollowupForm && (
                <form onSubmit={handleAddFollowup} className="space-y-3 mb-4 pb-4 border-b border-border">
                  <div className="space-y-1.5"><Label className="text-xs">Date & Time</Label><Input type="datetime-local" value={followupForm.followup_datetime} onChange={e => setFollowupForm({ ...followupForm, followup_datetime: e.target.value })} required data-testid="followup-datetime" className="[&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Note</Label><Input value={followupForm.note} onChange={e => setFollowupForm({ ...followupForm, note: e.target.value })} data-testid="followup-note" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Assign To</Label>
                    <UserPicker value={followupForm.assigned_to} onChange={v => setFollowupForm({ ...followupForm, assigned_to: v })} placeholder="Search employees..." data-testid="followup-assigned" />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="followup-update-status">
                    <input type="checkbox" checked={followupForm.update_status} onChange={e => setFollowupForm({ ...followupForm, update_status: e.target.checked })} className="accent-primary" />
                    Also update status to "Follow Up"
                  </label>
                  <Button type="submit" size="sm" data-testid="followup-submit">Schedule</Button>
                </form>
              )}

              {followups.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No follow-ups</p>
              ) : (
                <div className="space-y-3">
                  {followups.map(f => (
                    <div key={f.id} className={`p-3 rounded-lg border ${f.status === 'completed' ? 'border-green-500/20 bg-green-500/5' : isOverdue(f.followup_datetime) && f.status === 'pending' ? 'border-red-500/30 bg-red-500/5' : 'border-border bg-muted/20'}`} data-testid={`followup-item-${f.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium flex items-center gap-1.5">
                            {f.status === 'completed' && <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />}
                            {f.status === 'pending' && isOverdue(f.followup_datetime) && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                            {formatDateTime(f.followup_datetime)}
                          </p>
                          {f.note && <p className="text-xs text-muted-foreground mt-1">{f.note}</p>}
                          <p className="text-xs text-muted-foreground mt-0.5">{f.assigned_to_name || ''}</p>
                        </div>
                        {f.status === 'pending' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleCompleteFollowup(f.id)} data-testid={`complete-followup-${f.id}`}>Done</Button>
                        )}
                      </div>
                      <Badge className={`mt-2 text-xs ${f.status === 'completed' ? 'bg-green-500/15 text-green-400' : f.status === 'missed' ? 'bg-red-500/15 text-red-400' : 'bg-yellow-500/15 text-yellow-400'}`}>{f.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Status Audit Trail */}
      <Card data-testid="status-audit-trail">
        <CardHeader className="pb-3"><CardTitle className="text-base">Status History / Audit Trail</CardTitle></CardHeader>
        <CardContent>
          {statusHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No status changes recorded yet</p>
          ) : (
            <div className="relative space-y-0">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
              {statusHistory.map((h, i) => {
                const isEvent = !!h.event;
                const EventIcon = EVENT_ICONS[h.event] || Sparkles;
                const actorLabel = formatActor(h.changed_by, h.changed_by_name);
                return (
                  <div key={h.id} className="relative pl-10 pb-5" data-testid={`status-history-${i}`}>
                    <div className={`absolute left-2 top-0.5 h-5 w-5 rounded-full flex items-center justify-center z-10 ${isEvent ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                      {isEvent ? <EventIcon className="h-3 w-3" /> : <ArrowRight className="h-3 w-3" />}
                    </div>
                    {isEvent ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium" data-testid={`event-label-${h.event}`}>{h.event_label || h.event}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`${STATUS_COLORS[h.from_status] || 'bg-gray-500/15 text-gray-400'} border text-xs`}>{statuses.find(s => s.value === h.from_status)?.label || h.from_status || '—'}</Badge>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        <Badge className={`${STATUS_COLORS[h.to_status] || 'bg-gray-500/15 text-gray-400'} border text-xs`}>{statuses.find(s => s.value === h.to_status)?.label || h.to_status}</Badge>
                      </div>
                    )}
                    {h.remark && <p className="text-sm mt-1 whitespace-pre-wrap">{h.remark}</p>}
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {actorLabel} &middot; {formatDateTime(h.changed_at)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Change Modal */}
      <Dialog open={statusModal} onOpenChange={o => { if (!o) setStatusModal(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="detail-status-modal">
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
            <DialogDescription>{lead.business_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusValue} onValueChange={setStatusValue}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{statuses.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {PREVIEW_URL_STATUSES.includes(statusValue) && (
              <div className="space-y-2">
                <Label>Preview URL {PREVIEW_URL_STATUSES.includes(statusValue) && <span className="text-red-400">*</span>}</Label>
                <Input value={statusPreviewUrl} onChange={e => setStatusPreviewUrl(e.target.value)} placeholder="https://preview.myzow.com/..." data-testid="status-preview-url" />
              </div>
            )}
            {LIVE_URL_STATUSES.includes(statusValue) && (
              <div className="space-y-2">
                <Label>Live URL {LIVE_URL_STATUSES.includes(statusValue) && <span className="text-red-400">*</span>}</Label>
                <Input value={statusLiveUrl} onChange={e => setStatusLiveUrl(e.target.value)} placeholder="https://example.com" data-testid="status-live-url" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Remark / Note</Label>
              <Textarea value={statusRemark} onChange={e => setStatusRemark(e.target.value)} placeholder="Add a remark for this status change..." rows={3} data-testid="detail-status-remark" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusModal(false)}>Cancel</Button>
            <Button onClick={handleStatusChange} data-testid="detail-status-submit">Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Outreach Confirmation */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent data-testid="reset-outreach-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-500" />
              Reset outreach &amp; retry M1?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This will reset the outreach job for <strong>{lead.business_name}</strong> back to <strong>Message 1</strong> and send M1 <strong>immediately</strong>, even if the lead has previously replied.
              </span>
              <span className="block text-amber-600 dark:text-amber-400 text-sm">
                Chat history stays intact. The "responded" pause flag is cleared so the cadence runs end-to-end. Only proceed if you actually want to re-engage this lead.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetOutreach}
              disabled={outreachBusy}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="reset-confirm-btn"
            >
              {outreachBusy ? <Spinner className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Yes, reset &amp; retry
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual payment reconciliation — for rescuing leads whose
          Razorpay payment succeeded but didn't auto-advance. */}
      <Dialog open={reconcileOpen} onOpenChange={(o) => {
        setReconcileOpen(o);
        if (!o) { setReconcilePayId(''); setReconcileResult(null); }
      }}>
        <DialogContent data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle>Reconcile payment</DialogTitle>
            <DialogDescription>
              Paste the Razorpay payment id of a successful payment this customer made.
              The backend will verify it via Razorpay API and advance this lead to
              <span className="font-semibold"> Activation Paid </span>
              if the payment is captured.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-xs font-medium">Razorpay payment id</label>
            <input
              value={reconcilePayId}
              onChange={(e) => setReconcilePayId(e.target.value)}
              placeholder="pay_XXXXXXXXXXXXX"
              className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm font-mono"
              data-testid="reconcile-payment-id"
            />
            <p className="text-[10px] text-muted-foreground">
              Find this in Razorpay Dashboard → Payments. Looks like <code className="bg-muted px-1 rounded">pay_OkXxXxXxXxXxXx</code>.
            </p>
            {reconcileResult && (
              <div className={`rounded p-2 text-[11px] font-mono ${
                reconcileResult.ok ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
              }`} data-testid="reconcile-result">
                <div>ok: {String(reconcileResult.ok)}</div>
                {reconcileResult.razorpay_status && <div>rzp_status: {reconcileResult.razorpay_status}</div>}
                {reconcileResult.amount_paise && <div>amount: ₹{(reconcileResult.amount_paise/100).toFixed(2)}</div>}
                {reconcileResult.purpose && <div>purpose: {reconcileResult.purpose}</div>}
                {reconcileResult.smb_project_id && <div>project: {reconcileResult.smb_project_id.slice(0,8)}…</div>}
                {reconcileResult.duplicate && <div className="text-amber-400">Already reconciled (idempotent)</div>}
                {reconcileResult.error && <div className="break-all">err: {reconcileResult.error}</div>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconcileOpen(false)} data-testid="reconcile-cancel">
              Close
            </Button>
            <Button onClick={handleReconcilePayment} disabled={reconcileBusy} data-testid="reconcile-submit">
              {reconcileBusy ? <><Spinner className="h-3.5 w-3.5 mr-1.5 animate-spin" />Verifying…</> : 'Reconcile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual off-gateway payment (NEFT / UPI direct / Cash / Cheque).
          Auto-generates a GST tax invoice + income transaction. */}
      <ManualPaymentDialog
        open={manualPayOpen}
        onOpenChange={(v) => { setManualPayOpen(v); if (!v) setManualPayAddon(null); }}
        lead={lead}
        pkg={smbPackage}
        presetAddon={manualPayAddon}
        onSuccess={() => {
          fetchLead();
          fetchActivities();
          fetchStatusHistory();
        }}
      />

      {/* Combined off-gateway payment — one UTR → multiple line items */}
      <CombinedPaymentDialog
        open={combinedPayOpen}
        onOpenChange={setCombinedPayOpen}
        lead={lead}
        addons={leadAddons}
        onSuccess={() => { fetchLead(); fetchActivities(); fetchStatusHistory(); }}
      />
    </div>
  );
};
